import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  getActiveSuperAdminCount,
  getAdminById,
  updateAdmin,
  updateAdminRole,
} from "../../../redux/admin/action";
import { usePermissions } from "../../../Hooks/usePermissions";
import AdminForm from "./AdminForm";

const UpdateAdmin = ({ readOnly = false }) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isSuperAdmin, currentUserId } = usePermissions();
  const [submitting, setSubmitting] = useState(false);
  const [roleSubmitting, setRoleSubmitting] = useState(false);
  const [activeSuperAdminCount, setActiveSuperAdminCount] = useState(null);

  const admin = useSelector(({ admin: { updateadmin } }) => updateadmin);

  const numericId = Number(id);
  const isSelfEdit = Number.isFinite(numericId) && numericId === currentUserId;
  const showPermissions = isSuperAdmin && !isSelfEdit;

  useEffect(() => {
    if (id) dispatch(getAdminById(id));
  }, [dispatch, id]);

  // Only super admins editing another admin need the demote-guard count.
  useEffect(() => {
    if (!showPermissions) return;
    let cancelled = false;
    getActiveSuperAdminCount().then((count) => {
      if (!cancelled) setActiveSuperAdminCount(count);
    });
    return () => {
      cancelled = true;
    };
  }, [showPermissions]);

  const handleSubmit = (values) => {
    const payload = {
      id: numericId,
      first_name: values.first_name,
      last_name: values.last_name,
      email_id: values.email_id,
      country_code: values.country_code,
      mobile_number: values.mobile_number,
      status: Number(values.status),
    };
    if (values.password) payload.password = values.password;
    if (showPermissions) payload.permissions = values.permissions;

    dispatch(
      updateAdmin(payload, setSubmitting, navigate, {
        redirectTo: isSelfEdit ? null : "/admin",
        isSelf: isSelfEdit,
      }),
    );
  };

  const handleRoleChange = useCallback(
    (adminId, nextIsSuperAdmin) =>
      dispatch(
        updateAdminRole(adminId, nextIsSuperAdmin, {
          setLoading: setRoleSubmitting,
          isSelf: isSelfEdit,
          onSuccess: () => {
            dispatch(getAdminById(adminId));
            getActiveSuperAdminCount().then((count) => {
              if (count !== null) setActiveSuperAdminCount(count);
            });
          },
        }),
      ),
    [dispatch, isSelfEdit],
  );

  return (
    <AdminForm
      mode="update"
      title={
        readOnly
          ? "Admin profile"
          : isSelfEdit
            ? "My profile"
            : "Edit admin"
      }
      initialAdmin={admin}
      isSelfEdit={isSelfEdit}
      showPermissions={showPermissions}
      submitting={submitting}
      onSubmit={handleSubmit}
      backPath={readOnly ? "/admin/history" : isSelfEdit ? "/dashboard" : "/admin"}
      onRoleChange={handleRoleChange}
      roleSubmitting={roleSubmitting}
      activeSuperAdminCount={activeSuperAdminCount}
      readOnly={readOnly}
    />
  );
};

export default UpdateAdmin;
